'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { usePathname } from 'next/navigation';
import { Menu, X, Calendar, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { cn } from '@kit/ui/utils';
import { RyzeHeaderAccountSection } from './header-account-section';

const navLinks = [
  { labelKey: 'landing:nav.home', fallback: 'Home', href: '/' },
  { labelKey: 'landing:nav.about', fallback: 'About', href: '/#about' },
  { labelKey: 'landing:nav.location', fallback: 'Location', href: '/#location' },
];

const authNavLinks = [
  { labelKey: 'landing:nav.myBookings', fallback: 'My Bookings', href: '/bookings', icon: Calendar },
  { labelKey: 'landing:nav.myPackages', fallback: 'My Packages', href: '/packages', icon: Package },
];

const solidNavbarRoutes = [
  '/book',
  '/auth',
  '/bookings',
  '/profile',
  '/packages',
  '/payment',
  '/contact',
  '/privacy-policy',
  '/terms-of-service',
  '/coaches',
  '/membership',
];

export function RyzeHeader() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const session = useSession();
  const isAuthenticated = !!session.data;

  const isLandingPage = pathname === '/';
  const shouldBeSolid = solidNavbarRoutes.some((route) =>
    pathname?.startsWith(route),
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen]);

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          mobileMenuOpen
            ? 'bg-[var(--ryze-bg-deep)] pt-3 pb-1'
            : shouldBeSolid || scrolled
              ? 'bg-[var(--ryze-bg-deep)]/95 backdrop-blur py-3 border-b border-white/5'
              : 'bg-transparent py-5',
        )}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="relative z-10 flex-shrink-0">
            <Image
              src="/landing-pages/ryze/images/logo-header.png"
              alt="Ryze Tennis Academy"
              width={257}
              height={129}
              className="h-14 lg:h-16 w-auto"
              priority
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-white/90 hover:text-[var(--ryze-lime)] transition-colors duration-300 text-sm tracking-wide font-medium whitespace-nowrap"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated &&
              authNavLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-white/90 hover:text-[var(--ryze-lime)] transition-colors duration-300 text-sm tracking-wide font-medium whitespace-nowrap"
                >
                  {t(link.labelKey, { defaultValue: link.fallback })}
                </Link>
              ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center">
            <RyzeHeaderAccountSection />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white p-2 -mr-2"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 top-[56px] z-40 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
        )}

        {/* Mobile Menu */}
        <div
          className={cn(
            'lg:hidden absolute top-full left-0 right-0 bg-[var(--ryze-bg-deep)] transition-all duration-300 overflow-hidden z-50',
            mobileMenuOpen ? 'max-h-[500px]' : 'max-h-0',
          )}
        >
          <nav className="flex flex-col pb-4 px-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-white/90 hover:text-[var(--ryze-lime)] transition-colors duration-300 text-base font-medium py-3 border-b border-white/5"
              >
                {t(link.labelKey, { defaultValue: link.fallback })}
              </Link>
            ))}
            {isAuthenticated && (
              <>
                {authNavLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 text-white/90 hover:text-[var(--ryze-lime)] transition-colors duration-300 text-base font-medium py-3 border-b border-white/5"
                    >
                      <Icon className="h-5 w-5" />
                      {t(link.labelKey, { defaultValue: link.fallback })}
                    </Link>
                  );
                })}
              </>
            )}
            <div className="pt-4">
              <RyzeHeaderAccountSection
                onNavigate={() => setMobileMenuOpen(false)}
              />
            </div>
          </nav>
        </div>
      </header>
      {/* Spacer for fixed header - only on non-landing pages */}
      {!isLandingPage && <div className="h-[64px]" />}
    </>
  );
}

function useSession() {
  const client = useSupabase();
  const queryClient = useQueryClient();

  useEffect(() => {
    const { data: listener } = client.auth.onAuthStateChange(() => {
      queryClient.invalidateQueries({ queryKey: ['session'] });
    });

    return () => listener.subscription.unsubscribe();
  }, [client.auth, queryClient]);

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}
